import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOWN_ARROW, ENTER, ESCAPE, SPACE, UP_ARROW } from '@angular/cdk/keycodes';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, ScrollStrategy } from '@angular/cdk/overlay';
import { ViewportRuler } from '@angular/cdk/scrolling';
import {
  AfterContentInit,
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  InjectionToken,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher, MAT_OPTION_PARENT_COMPONENT, MatOption, MatOptionSelectionChange } from '@angular/material/core';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { defer, merge, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  _AbstractConstructor,
  _Constructor,
  CanUpdateErrorState,
  mixinErrorState,
  CanDisable,
  HasTabIndex,
  mixinDisabled,
  mixinTabIndex,
} from '@nimbus-library/utils/material-mixins-types';
import { convertFromTeamsClientCoordinates, isContainsInSVG } from '@nimbus-library/features/shared/components/material/utils';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';
import { matDropDownAnimations } from './mat-drop-down.animations';

let nextUniqueId = 0;

class MatSelectBase {
  public readonly stateChanges: Subject<void> = new Subject<void>();

  public constructor(
    public _elementRef: ElementRef,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public ngControl: NgControl,
  ) {}
}

const MatDropDownMixinBase: _Constructor<HasTabIndex> &
  _AbstractConstructor<HasTabIndex> &
  _Constructor<CanDisable> &
  _AbstractConstructor<CanDisable> &
  _Constructor<CanUpdateErrorState> &
  _AbstractConstructor<CanUpdateErrorState> &
  typeof MatSelectBase = mixinTabIndex(mixinDisabled(mixinErrorState(MatSelectBase)));

export const MAT_DROP_DOWN_TRIGGER = new InjectionToken<MatDropDownTriggerDirective>('MatDropDownTriggerDirective');

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'nimbus-mat-drop-down-trigger',
  providers: [{ provide: MAT_DROP_DOWN_TRIGGER, useExisting: MatDropDownTriggerDirective }],
  standalone: false,
})
export class MatDropDownTriggerDirective {}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-mat-drop-down',
  templateUrl: 'mat-drop-down.component.html',
  styleUrls: ['./mat-drop-down.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mat-drop-down',
    '[attr.id]': 'id',
    '[attr.tabindex]': 'tabIndex',
    '[class.mat-drop-down-disabled]': 'disabled',
    '[class.mat-drop-down-required]': 'required',
    '[class.mat-drop-down-empty]': 'empty',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()',
    '[attr.aria-invalid]': 'errorState',
    '[class.mat-drop-down-invalid]': 'errorState',
  },
  animations: [matDropDownAnimations.transformPanelWrap, matDropDownAnimations.transformPanel],
  providers: [
    { provide: MatFormFieldControl, useExisting: MatDropDownComponent },
    { provide: MAT_OPTION_PARENT_COMPONENT, useExisting: MatDropDownComponent },
  ],
})
export class MatDropDownComponent
  extends MatDropDownMixinBase
  implements
    AfterContentInit,
    OnDestroy,
    OnInit,
    DoCheck,
    ControlValueAccessor,
    CanDisable,
    HasTabIndex,
    MatFormFieldControl<any>,
    CanUpdateErrorState
{
  @Input()
  public disabled: boolean;
  @Input()
  public errorStateMatcher: ErrorStateMatcher;
  @Input()
  public panelClass: string;
  @Input()
  public tabIndex: number;
  @Output()
  public readonly openedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('trigger', { static: true })
  public trigger: ElementRef;
  @ViewChild('panel')
  public panel: ElementRef;
  @ViewChild(CdkConnectedOverlay, { static: true })
  public overlayDir: CdkConnectedOverlay;
  @ContentChildren(MatOption, { descendants: true })
  public options: QueryList<MatOption>;
  @ContentChild(MAT_DROP_DOWN_TRIGGER as any)
  public customTrigger: MatDropDownTriggerDirective;

  private readonly _scrollStrategyFactory: () => ScrollStrategy;
  private _keyManager: ActiveDescendantKeyManager<MatOption>;
  private _scrollTop = 0;
  private _uid = `mat-drop-down-${nextUniqueId++}`;
  private readonly _destroy = new Subject<void>();
  private innerPlaceholder: string;
  private isFocused: boolean = false;
  private isRequired: boolean = false;
  private innerValue: any;
  private _id: string;

  private readonly optionSelectionChanges: Observable<MatOptionSelectionChange> = defer(() => {
    const options = this.options;

    if (options) {
      return options.changes.pipe(
        startWith(options),
        switchMap(() => merge(...options.map((option) => option.onSelectionChange))),
      );
    }

    return this._ngZone.onStable.pipe(
      take(1),
      switchMap(() => this.optionSelectionChanges),
    );
  }) as Observable<MatOptionSelectionChange>;

  public _triggerRect: ClientRect;
  public _ariaDescribedby: string;
  public readonly autofilled: boolean;
  public readonly controlType: string;
  public defaultTabIndex: number;
  public readonly userAriaDescribedBy: string;
  public _panelDoneAnimatingStream = new Subject<string>();
  public _scrollStrategy: ScrollStrategy;
  public selectedOption: MatOption;
  public _offsetY = 0;
  public isPanelOpen: boolean = false;
  public isScalable: boolean = false;
  public _positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  public teamsClient$: Observable<boolean> = this.microsoftTeamsContextAccessor
    ? this.microsoftTeamsContextAccessor.getClientInfo$().pipe(
        take(1),
        map((x) => x !== ClientType.Browser),
      )
    : of(false);

  public get focused(): boolean {
    return this.isFocused || this.isPanelOpen;
  }

  @Input()
  public get placeholder(): string {
    return this.innerPlaceholder;
  }

  public set placeholder(value: string) {
    this.innerPlaceholder = value;
    this.stateChanges.next();
  }

  @Input()
  public get required(): boolean {
    return this.isRequired;
  }

  public set required(value: boolean) {
    this.isRequired = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  public get value(): any {
    return this.innerValue;
  }

  public set value(value: any) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.setSelectionByValue();
      this.propagateChange(value);
    }
  }

  @Input()
  public get id(): string {
    return this._id;
  }

  public set id(value: string) {
    this._id = value || this._uid;
    this.stateChanges.next();
  }

  public constructor(
    private _viewportRuler: ViewportRuler,
    private changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    private elementReference: ElementRef<HTMLElement>,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    @Optional() private _parentFormField: MatFormField,
    @Self() @Optional() public ngControl: NgControl,
    @Attribute('tabindex') tabIndex: string,
    @Inject(MAT_SELECT_SCROLL_STRATEGY) scrollStrategyFactory: any,
    private readonly microsoftTeamsContextAccessor: IMicrosoftTeamsContextAccessor,
  ) {
    super(elementReference, _defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this._scrollStrategyFactory = scrollStrategyFactory;
    this._scrollStrategy = this._scrollStrategyFactory();

    this.tabIndex = parseInt(tabIndex) || 0;
  }

  public propagateChange: any = () => {};
  public propagateTouched: any = () => {};

  public ngOnInit(): void {
    this._panelDoneAnimatingStream.pipe(distinctUntilChanged(), takeUntil(this._destroy)).subscribe(() => {
      if (this.isPanelOpen) {
        this._scrollTop = 0;
        this.openedChange.emit(true);
      } else {
        this.openedChange.emit(false);
        this.overlayDir.offsetX = 0;
        this.changeDetectorRef.markForCheck();
      }
    });

    this._viewportRuler
      .change()
      .pipe(takeUntil(this._destroy))
      .subscribe(() => {
        if (this.isPanelOpen) {
          this._triggerRect = this.getOverlayTriggerRect();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public ngAfterContentInit(): void {
    this.options.changes.pipe(startWith(null), takeUntil(this._destroy)).subscribe(() => {
      this.resetOptions();
      this.setSelectionByValue();
    });
  }

  public setSelectionByValue(): void {
    this.selectedOption = null;
    (this.options || []).forEach((option) => {
      if (option.value === this.innerValue) {
        option.select();
        this.selectedOption = option;
      } else {
        option.deselect();
      }
    });
    this.changeDetectorRef.detectChanges();
  }

  public ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  public togglePanel(): void {
    this.isPanelOpen ? this.close() : this.open();
  }

  public open(): void {
    if (this.disabled || !this.options || !this.options.length || this.isPanelOpen) {
      return;
    }
    this.setSelectionByValue();
    this._triggerRect = this.getOverlayTriggerRect();

    this.isPanelOpen = true;
    this._calculateOverlayPosition();
    this._keyManager?.setActiveItem(this.selectedOption ?? 0);
    this.changeDetectorRef.markForCheck();
  }

  // Arrow keys move the active (highlighted) option via the CDK key manager without selecting it -
  // the same pattern MatSelect uses - so a value is only committed on Enter/Space, matching the
  // click behaviour wired up in resetOptions().
  @HostListener('keydown', ['$event'])
  public _handleKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    if (!this.isPanelOpen) {
      if ([DOWN_ARROW, UP_ARROW, ENTER, SPACE].includes(event.keyCode)) {
        event.preventDefault();
        this.open();
      }
      return;
    }

    if (event.keyCode === ESCAPE) {
      event.preventDefault();
      this.close();
      this.focus();
      return;
    }

    if (event.keyCode === ENTER || event.keyCode === SPACE) {
      event.preventDefault();
      const activeOption = this._keyManager?.activeItem;
      if (activeOption && !activeOption.disabled) {
        activeOption._selectViaInteraction();
      }
      return;
    }

    this._keyManager?.onKeydown(event);
    this._keyManager?.activeItem?._getHostElement().scrollIntoView({ block: 'nearest' });
  }

  /** Closes the overlay panel and focuses the host element. */
  public close(): void {
    if (this.isPanelOpen) {
      this.isPanelOpen = false;
      this.changeDetectorRef.markForCheck();
      this.propagateTouched();
    }
  }

  public writeValue(value: any): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      if (this.options) {
        this.setSelectionByValue();
      }
    }
    this.changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.propagateTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
    this.stateChanges.next();
  }

  public get triggerValue(): string {
    if (this.empty) {
      return '';
    }
    if (this.selectedOption) {
      return this.selectedOption.viewValue;
    } else {
      return '';
    }
  }

  public onFocus(): void {
    if (!this.disabled) {
      this.isFocused = true;
      this.stateChanges.next();
    }
  }

  public onBlur(): void {
    this.isFocused = false;

    if (!this.disabled && !this.isPanelOpen) {
      this.propagateTouched();
      this.changeDetectorRef.markForCheck();
      this.stateChanges.next();
    }
  }

  public onAttached(): void {
    this.overlayDir.positionChange.pipe(take(1)).subscribe(() => {
      this.changeDetectorRef.detectChanges();
      this._calculateOverlayOffsetX();
      this.panel.nativeElement.scrollTop = this._scrollTop;
    });
  }

  public setDescribedByIds(ids: string[]) {
    this._ariaDescribedby = ids.join(' ');
  }

  public onContainerClick() {
    this.focus();
    this.open();
  }

  public get shouldLabelFloat(): boolean {
    return this.isPanelOpen || !this.empty;
  }

  public focus(options?: FocusOptions): void {
    this._elementRef.nativeElement.focus(options);
  }

  public ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this.stateChanges.complete();
    this._keyManager?.destroy();
  }

  public updateErrorState(): void {
    super.updateErrorState();
  }

  public get empty(): boolean {
    return !this.innerValue && this.innerValue !== 0;
  }

  private resetOptions(): void {
    const changedOrDestroyed = merge(this.options.changes, this._destroy);

    this._keyManager = new ActiveDescendantKeyManager(this.options).withVerticalOrientation().withTypeAhead().withWrap();

    this.optionSelectionChanges.pipe(takeUntil(changedOrDestroyed)).subscribe((event) => {
      if (event.isUserInput && this.isPanelOpen) {
        this.selectOption(event.source);
        this.close();
        this.focus();
      }
    });

    merge(...this.options.map((option) => option._stateChanges))
      .pipe(takeUntil(changedOrDestroyed))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
        this.stateChanges.next();
      });
  }

  private selectOption(option: MatOption): void {
    this.options.forEach((optionItem) => {
      optionItem.deselect();
    });
    this.value = option.value;
    this.stateChanges.next();
  }

  private _calculateOverlayPosition(): void {
    const itemHeight = this._getItemHeight();
    const items = this._getItemCount();

    this.isScalable = isContainsInSVG(this.elementReference.nativeElement);
    const globalScale: number = this.isScalable ? +getComputedStyle(document.body).getPropertyValue('--global- scale') : 1;

    const panelMaxHeight: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-select-panel-max-height') || '256');

    const panelHeight = Math.min(items * itemHeight, panelMaxHeight);
    const scrollContainerHeight = items * itemHeight;

    const maxScroll = scrollContainerHeight - panelHeight;
    const paddingTop = 28;

    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        if (this.isScalable) {
          const currentScaledPositionY = (this.overlayDir.origin as CdkOverlayOrigin).elementRef.nativeElement.getBoundingClientRect().y;
          const currentPositionY = convertFromTeamsClientCoordinates(currentScaledPositionY);
          this._offsetY =
            -1 * (currentScaledPositionY - currentPositionY - paddingTop * globalScale - (panelHeight * globalScale - panelHeight) / 2);
        } else {
          this._offsetY = paddingTop * globalScale + (panelHeight * globalScale - panelHeight) / 2;
        }
      } else {
        this._offsetY = paddingTop * globalScale + (panelHeight * globalScale - panelHeight) / 2;
      }
      this._scrollTop = 0;
      this._checkOverlayWithinViewport(maxScroll);
    });
  }

  private _checkOverlayWithinViewport(maxScroll: number): void {
    const itemHeight = this._getItemHeight();
    const viewportSize = this._viewportRuler.getViewportSize();

    const topSpaceAvailable = this._triggerRect.top;
    const bottomSpaceAvailable = viewportSize.height - this._triggerRect.bottom;

    const panelHeightTop = Math.abs(this._offsetY);

    const panelMaxHeight: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-select-panel-max-height') || '256');

    const totalPanelHeight = Math.min(this._getItemCount() * itemHeight, panelMaxHeight);
    const panelHeightBottom = totalPanelHeight - panelHeightTop - this._triggerRect.height;

    if (panelHeightBottom > bottomSpaceAvailable) {
      this.adjustPanelUp(panelHeightBottom, bottomSpaceAvailable);
    } else if (panelHeightTop > topSpaceAvailable) {
      this.adjustPanelDown(panelHeightTop, topSpaceAvailable, maxScroll);
    }
  }

  private adjustPanelUp(panelHeightBottom: number, bottomSpaceAvailable: number) {
    const distanceBelowViewport = Math.round(panelHeightBottom - bottomSpaceAvailable);

    this._scrollTop -= distanceBelowViewport;
    this._offsetY -= distanceBelowViewport;

    if (this._scrollTop <= 0) {
      this._scrollTop = 0;
      this._offsetY = 0;
    }
  }

  private adjustPanelDown(panelHeightTop: number, topSpaceAvailable: number, maxScroll: number) {
    const distanceAboveViewport = Math.round(panelHeightTop - topSpaceAvailable);
    this._scrollTop += distanceAboveViewport;
    this._offsetY += distanceAboveViewport;

    if (this._scrollTop >= maxScroll) {
      this._scrollTop = maxScroll;
      this._offsetY = 0;
    }
  }

  private _calculateOverlayOffsetX(): void {
    const overlayRect = this.overlayDir.overlayRef.overlayElement.getBoundingClientRect();
    const viewportSize = this._viewportRuler.getViewportSize();

    this.isScalable = isContainsInSVG(this.elementReference.nativeElement);
    const globalScale: number = this.isScalable ? +getComputedStyle(document.body).getPropertyValue('--global- scale') : 1;

    let xPadding: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-field-flex-padding ') || '12');

    const fieldWidth: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-field-width') || '300');

    xPadding = xPadding * globalScale;

    const paddingWidth = xPadding * 2;
    let offsetX: number;

    offsetX = xPadding;

    offsetX *= -1;

    // Determine how much the select overflows on each side.

    let leftOverflow = 0 - (overlayRect.left + offsetX);
    let rightOverflow = overlayRect.right + offsetX - viewportSize.width + paddingWidth;

    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        leftOverflow = 0 - (convertFromTeamsClientCoordinates(overlayRect.left) + offsetX);
        rightOverflow = convertFromTeamsClientCoordinates(overlayRect.right) + offsetX - viewportSize.width + paddingWidth;
      }
      if (leftOverflow > 0) {
        offsetX += leftOverflow;
      } else if (rightOverflow > 0) {
        offsetX -= rightOverflow;
      }
      if (isTeamsClient) {
        if (this.isScalable) {
          const currentScaledPositionX = (this.overlayDir.origin as CdkOverlayOrigin).elementRef.nativeElement.getBoundingClientRect().x;
          const currentPositionX = convertFromTeamsClientCoordinates(currentScaledPositionX);
          this.overlayDir.offsetX =
            -1 * (currentScaledPositionX - currentPositionX - offsetX - (fieldWidth * globalScale - fieldWidth) / 2);
        } else {
          this.overlayDir.offsetX = offsetX + (fieldWidth * globalScale - fieldWidth) / 2;
        }
      } else {
        this.overlayDir.offsetX = offsetX + (fieldWidth * globalScale - fieldWidth) / 2;
      }

      this.overlayDir.overlayRef.updatePosition();
    });
  }

  private getOverlayTriggerRect(): ClientRect {
    const triggerRect = this.trigger.nativeElement.getBoundingClientRect();
    const fieldWrapperElement = this.trigger.nativeElement.closest('.mat-mdc-text-field-wrapper') as HTMLElement | null;

    if (!fieldWrapperElement) {
      return triggerRect;
    }

    const fieldWrapperWidth = fieldWrapperElement.getBoundingClientRect().width;

    return {
      top: triggerRect.top,
      left: triggerRect.left,
      bottom: triggerRect.bottom,
      height: triggerRect.height,
      width: fieldWrapperWidth,
      right: triggerRect.left + fieldWrapperWidth,
    } as ClientRect;
  }

  private _getItemCount(): number {
    return this.options.length;
  }

  private _getItemHeight(): number {
    return 31;
  }
}
